import React, { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { adminApi } from '../../services/api';
import { Skeleton } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { formatDateTime } from '../../utils/helpers';
import type { PaginatedResponse, Report } from '../../types';

const statusMeta: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pendiente', className: 'bg-warning-100 text-warning-700' },
  resolved: { label: 'Resuelto', className: 'bg-success-100 text-success-700' },
  dismissed: { label: 'Descartado', className: 'bg-secondary-100 text-secondary-600' },
};

export const AdminReports: React.FC = () => {
  const [result, setResult] = useState<PaginatedResponse<Report> | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [resolvingId, setResolvingId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    adminApi
      .reports({ status: status || undefined, page })
      .then((res) => setResult(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  const handleResolve = async (report: Report, newStatus: 'resolved' | 'dismissed') => {
    setResolvingId(report.id);
    try {
      await adminApi.resolveReport(report.id, newStatus);
      setResult((prev) =>
        prev ? { ...prev, data: prev.data.map((r) => (r.id === report.id ? { ...r, status: newStatus } : r)) } : prev
      );
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-onSurface">Reportes</h1>
        <p className="text-onSurface-mute">Revisá y resolvé reportes entre usuarios</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          className="px-3 py-2 text-sm rounded-xl border border-outlineVarWarm bg-surface text-onSurface focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">Todos los estados</option>
          {Object.entries(statusMeta).map(([value, meta]) => (
            <option key={value} value={value}>
              {meta.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-surface rounded-2xl border border-outlineVarWarm overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : result && result.data.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-outlineVarWarm text-left text-onSurface-mute">
                <th className="px-5 py-3 font-medium">Reportante</th>
                <th className="px-5 py-3 font-medium">Reportado</th>
                <th className="px-5 py-3 font-medium">Motivo</th>
                <th className="px-5 py-3 font-medium">Estado</th>
                <th className="px-5 py-3 font-medium">Fecha</th>
                <th className="px-5 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outlineVarWarm">
              {result.data.map((report) => (
                <tr key={report.id}>
                  <td className="px-5 py-3 text-onSurface">{report.reporter?.full_name || '—'}</td>
                  <td className="px-5 py-3 text-onSurface">{report.reported?.full_name || '—'}</td>
                  <td className="px-5 py-3 text-onSurface-var max-w-xs">
                    <p className="font-medium text-onSurface">{report.reason}</p>
                    {report.description && (
                      <p className="text-xs text-onSurface-mute truncate">{report.description}</p>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`badge ${statusMeta[report.status]?.className || ''}`}>
                      {statusMeta[report.status]?.label || report.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-onSurface-var">{formatDateTime(report.created_at)}</td>
                  <td className="px-5 py-3">
                    {report.status === 'pending' ? (
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleResolve(report, 'resolved')}
                          disabled={resolvingId === report.id}
                          className="p-2 rounded-lg text-success-600 hover:bg-success-50 transition-colors disabled:opacity-50"
                          title="Resolver"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResolve(report, 'dismissed')}
                          disabled={resolvingId === report.id}
                          className="p-2 rounded-lg text-onSurface-var hover:bg-surface-var transition-colors disabled:opacity-50"
                          title="Descartar"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-right text-xs text-onSurface-mute">—</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-onSurface-mute text-center py-12">No hay reportes</p>
        )}
      </div>

      {result && <Pagination meta={result} onPageChange={setPage} />}
    </div>
  );
};
